import { useMemo, useRef, useState } from "react";
import { useEditorStore, type StoreLayer } from "@/store";
import { LAYER_TYPE_COLORS, LAYER_TYPE_ICONS, type LayerType } from "@/styles/theme";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { LayerEditor } from "./LayerEditor";
import { AddLayerDialog } from "./AddLayerDialog";

export function LayersSection() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [groupBySource, setGroupBySource] = useState(false);
  const layers = useEditorStore((s) => s.style.layers);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const setSelectedLayerId = useEditorStore((s) => s.setSelectedLayerId);
  const setHoveredLayerId = useEditorStore((s) => s.setHoveredLayerId);
  const toggleLayerVisibility = useEditorStore((s) => s.toggleLayerVisibility);
  const removeLayer = useEditorStore((s) => s.removeLayer);
  const reorderLayer = useEditorStore((s) => s.reorderLayer);
  const currentZoom = useEditorStore((s) => s.viewport.zoom);

  const selectedLayer = selectedLayerId
    ? layers.find((l) => l.id === selectedLayerId)
    : null;

  const dragIdx = useRef<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const displayLayers = useMemo(() => [...layers].reverse(), [layers]);
  const realIndex = (vi: number) => layers.length - 1 - vi;

  const filtered = useMemo(() => {
    if (!search) return displayLayers;
    const q = search.toLowerCase();
    return displayLayers.filter(
      (l) =>
        l.id.toLowerCase().includes(q) ||
        l.type.toLowerCase().includes(q) ||
        (l.source ?? "").toLowerCase().includes(q) ||
        (l["source-layer"] ?? "").toLowerCase().includes(q),
    );
  }, [displayLayers, search]);

  // Build grouped structure if needed
  const grouped = useMemo(() => {
    if (!groupBySource) return null;
    const groups: { source: string; layers: { layer: StoreLayer; visualIdx: number }[] }[] = [];
    let current: (typeof groups)[number] | null = null;
    for (const layer of filtered) {
      const src = layer.source ?? "(no source)";
      const vi = displayLayers.indexOf(layer);
      if (!current || current.source !== src) {
        current = { source: src, layers: [] };
        groups.push(current);
      }
      current.layers.push({ layer, visualIdx: vi });
    }
    return groups;
  }, [filtered, groupBySource, displayLayers]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <InfoBanner storageKey="section-layers">
        Layers render in order — top draws on top. Drag to reorder. Hover to highlight on the map.
      </InfoBanner>

      <div className="mb-3 mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-4 w-1 rounded-radius-full bg-accent" />
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-primary">
            Layers ({layers.length})
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1 rounded-radius-sm border border-accent bg-accent/10 px-2.5 py-1 text-[11px] font-medium text-accent hover:bg-accent hover:text-accent-text transition-colors"
        >
          + Add
        </button>
      </div>

      {showAddDialog && <AddLayerDialog onClose={() => setShowAddDialog(false)} />}

      {!selectedLayer ? (
        layers.length === 0 ? (
          <EmptyState onAdd={() => setShowAddDialog(true)} />
        ) : (
          <>
            <div className="mb-2 flex items-center gap-1.5">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search layers..."
                className="h-7 flex-1 rounded-radius-sm border border-border-subtle bg-surface-sunken px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:bg-surface-base focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setGroupBySource(!groupBySource)}
                className={`h-7 shrink-0 rounded-radius-sm border px-2 text-[9px] font-medium transition-colors ${
                  groupBySource
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border-subtle text-text-muted hover:text-text-secondary"
                }`}
                title="Group layers by source"
              >
                Group
              </button>
            </div>

            <div className="space-y-0.5 overflow-y-auto">
              {grouped
                ? grouped.map((group) => (
                    <div key={group.source}>
                      <div className="mt-2 mb-1 flex items-center gap-1.5 px-1">
                        <span className="h-px flex-1 bg-border-subtle" />
                        <span className="text-[9px] font-medium text-text-muted">{group.source}</span>
                        <span className="shrink-0 text-[8px] text-text-muted/50">{group.layers.length}</span>
                        <span className="h-px flex-1 bg-border-subtle" />
                      </div>
                      {group.layers.map(({ layer, visualIdx }) => (
                        <LayerRow
                          key={layer.id}
                          layer={layer}
                          visualIdx={visualIdx}
                          selectedLayerId={selectedLayerId}
                          dropTarget={dropTarget}
                          currentZoom={currentZoom}
                          onSelect={setSelectedLayerId}
                          onHover={setHoveredLayerId}
                          onToggleVisibility={toggleLayerVisibility}
                          onDragStart={() => { dragIdx.current = realIndex(visualIdx); }}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(visualIdx); }}
                          onDrop={() => { const from = dragIdx.current; const to = realIndex(visualIdx); if (from !== null && from !== to) reorderLayer(from, to); dragIdx.current = null; setDropTarget(null); }}
                          onDragEnd={() => { dragIdx.current = null; setDropTarget(null); }}
                        />
                      ))}
                    </div>
                  ))
                : filtered.map((layer) => {
                    const vi = displayLayers.indexOf(layer);
                    return (
                      <LayerRow
                        key={layer.id}
                        layer={layer}
                        visualIdx={vi}
                        selectedLayerId={selectedLayerId}
                        dropTarget={dropTarget}
                        currentZoom={currentZoom}
                        onSelect={setSelectedLayerId}
                        onHover={setHoveredLayerId}
                        onToggleVisibility={toggleLayerVisibility}
                        onDragStart={() => { dragIdx.current = realIndex(vi); }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(vi); }}
                        onDrop={() => { const from = dragIdx.current; const to = realIndex(vi); if (from !== null && from !== to) reorderLayer(from, to); dragIdx.current = null; setDropTarget(null); }}
                        onDragEnd={() => { dragIdx.current = null; setDropTarget(null); }}
                      />
                    );
                  })}
            </div>
          </>
        )
      ) : (
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-border-subtle bg-surface-base pb-2">
            <button
              type="button"
              onClick={() => setSelectedLayerId(null)}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover"
            >
              ← All layers
            </button>
          </div>
          <div className="flex-1 overflow-y-auto pt-3">
            <LayerEditor
              layer={selectedLayer}
              onRemove={() => {
                if (selectedLayerId) removeLayer(selectedLayerId);
                setSelectedLayerId(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Layer Row ──

function LayerRow({
  layer,
  visualIdx,
  selectedLayerId,
  dropTarget,
  currentZoom,
  onSelect,
  onHover,
  onToggleVisibility,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  layer: StoreLayer;
  visualIdx: number;
  selectedLayerId: string | null;
  dropTarget: number | null;
  currentZoom: number;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onToggleVisibility: (id: string) => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const layerType = layer.type as LayerType;
  const isVisible = layer.layout?.visibility !== "none";
  const isDragOver = dropTarget === visualIdx;
  const minZ = layer.minzoom ?? 0;
  const maxZ = layer.maxzoom ?? 24;
  const zoomStatus =
    !isVisible ? "visible" :
    currentZoom < minZ ? "below" :
    currentZoom >= maxZ ? "above" :
    "visible";

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(layer.id)}
      className={`group flex cursor-pointer items-center gap-1.5 rounded-radius-sm px-2 py-1.5 transition-colors ${
        selectedLayerId === layer.id
          ? "bg-accent-muted"
          : isDragOver
            ? "bg-accent-muted/50 ring-1 ring-accent/40"
            : "hover:bg-surface-sunken"
      }`}
      onMouseEnter={() => onHover(layer.id)}
      onMouseLeave={() => onHover(null)}
    >
      <span
        className="shrink-0 cursor-grab text-[10px] text-text-muted/40 group-hover:text-text-muted active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        ⠿
      </span>
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-radius-sm text-[10px] text-white"
        style={{ backgroundColor: LAYER_TYPE_COLORS[layerType] ?? "var(--color-border-muted)" }}
      >
        {LAYER_TYPE_ICONS[layerType] ?? "?"}
      </span>
      <span
        className={`flex-1 truncate text-left text-xs font-medium ${
          !isVisible ? "text-error/60" : zoomStatus !== "visible" ? "text-text-muted" : "text-text-primary"
        }`}
      >
        {layer.id}
      </span>
      {zoomStatus === "below" && (
        <span className="shrink-0 text-[9px] text-warning" title={`Zoom in past z${minZ}`}>▲z{minZ}</span>
      )}
      {zoomStatus === "above" && (
        <span className="shrink-0 text-[9px] text-warning" title={`Zoom out below z${maxZ}`}>▼z{maxZ}</span>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(layer.id); }}
        className={`shrink-0 text-[10px] opacity-0 transition-opacity group-hover:opacity-100 ${
          isVisible ? "text-text-muted" : "text-error"
        }`}
        title={isVisible ? "Hide layer" : "Show layer"}
      >
        {isVisible ? "◉" : "○"}
      </button>
    </div>
  );
}

// ── Empty State ──

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <div className="text-2xl text-text-muted">◧</div>
      <div className="text-xs text-text-secondary">No layers yet</div>
      <p className="max-w-48 text-[10px] text-text-muted">
        Layers control how source data is drawn on the map. Add a source first, then create layers to visualize it.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="flex items-center gap-1 rounded-radius-sm border border-accent bg-accent/10 px-3 py-1.5 text-[11px] font-medium text-accent hover:bg-accent hover:text-accent-text transition-colors"
      >
        + Add first layer
      </button>
    </div>
  );
}
